import React, { useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Chip, Box, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" sx={{ color: '#1976d2' }} />;

// Modern stil tanımları
const styles = {
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: '#64748b',
      '&.Mui-focused': {
        color: '#1976d2',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0',
    },
  },
  optionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 4px',
    borderRadius: '8px',
    margin: '2px 0',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '8px',
  },
  optionPrimary: {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#1e293b',
  },
  optionSecondary: {
    fontSize: '0.75rem',
    color: '#64748b',
  },
  chip: {
    borderRadius: '8px',
    fontWeight: 500,
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    border: '1px solid #bae6fd',
    '& .MuiChip-deleteIcon': {
      color: '#0369a1',
      '&:hover': {
        color: '#0c4a6e',
      },
    },
  },
  listbox: {
    padding: '8px',
    '& .MuiAutocomplete-option': {
      borderRadius: '8px',
      margin: '2px 0',
    },
  },
};

export default function BagliUrunAutocomplete({
  data,
  type,
  fisNo,
  bagliUrunData,
  setBagliUrunData
}) {
  const urunData =
    type === 17
      ? data.Receipts.filter(
          x => [15, 16].includes(x.Type) || (x.Level === 1 && x.FisNo === 0)
        )
      : data.Receipts.filter(x => x.Level === 1 && x.FisNo === 0);

  const bagliUrunFisNos = bagliUrunData
    .filter(item => item.BagliOlduguFisNo === fisNo)
    .map(item => (item.FisNo !== 0 ? item.FisNo : item.DetailId));

  const mappedValue = urunData.filter(urun =>
    bagliUrunFisNos.includes(urun.FisNo !== 0 ? urun.FisNo : urun.DetailId)
  );

  const [selectedValue, setSelectedValue] = useState(mappedValue);

  const handleChange = (event, value, rowFisNo) => {
    setSelectedValue(value);
    setBagliUrunData(prevData => {
      const newEntries =
        value.length === 0
          ? [
              {
                DetailId: 0,
                BagliOlduguFisNo: rowFisNo,
                FisNo: 0,
                Level: 0,
                SystemName: ''
              }
            ]
          : value.map(item => ({
              DetailId: item.DetailId,
              BagliOlduguFisNo: rowFisNo,
              FisNo: item.FisNo,
              Level: item.Level,
              SystemName: item.SystemName
            }));

      const filteredData = prevData.filter(
        data => data.BagliOlduguFisNo !== rowFisNo
      );

      return [...filteredData, ...newEntries];
    });
  };

  return (
    <Autocomplete
      multiple
      id="BagliUrunAutoSelect"
      options={urunData}
      value={selectedValue}
      disableCloseOnSelect
      fullWidth
      sx={styles.autocomplete}
      ListboxProps={{
        sx: styles.listbox,
      }}
      getOptionLabel={option =>
        option.FisNo !== 0 ? option.FisNo.toString() : option.SystemName
      }
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            <Box sx={styles.optionItem}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
                sx={{ padding: '4px' }}
              />
              <Box sx={styles.optionText}>
                <Typography sx={styles.optionPrimary}>
                  {option.FisNo !== 0 ? `Fiş No: ${option.FisNo}` : option.SystemName}
                </Typography>
                <Typography sx={styles.optionSecondary}>
                  {option.ModelDesc || 'Model bilgisi yok'}
                </Typography>
              </Box>
            </Box>
          </li>
        );
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.FisNo || option.DetailId}
            label={option.FisNo !== 0 ? `Fiş: ${option.FisNo}` : option.SystemName}
            size="small"
            icon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
            sx={styles.chip}
          />
        ))
      }
      renderInput={params => (
        <TextField
          {...params}
          label="Bağlı Ürünler"
          placeholder={selectedValue.length === 0 ? "Ürün seçiniz..." : ""}
          helperText={selectedValue.length > 0 ? `${selectedValue.length} ürün seçildi` : ""}
          FormHelperTextProps={{
            sx: { color: '#64748b', fontSize: '0.75rem', marginLeft: '4px' }
          }}
        />
      )}
      noOptionsText={
        <Box sx={{ padding: '16px', textAlign: 'center' }}>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Seçilebilecek ürün bulunamadı
          </Typography>
        </Box>
      }
      onChange={(event, value) => handleChange(event, value, fisNo)}
    />
  );
}
